import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { Typography, Grid, Stack, Box, Divider, Input, IconButton, InputAdornment, Menu, MenuItem } from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import BookTwoToneIcon from "@mui/icons-material/BookTwoTone"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import UserContext from "../../../context/UserContext"
import ChatDialog from "./ChatDialog"
import useFetch from "../../../hooks/useFetch"
import useMutation from "../../../hooks/useMutation"
import { F_TRADING_CHAT, F_TRADING_HISTORY } from "../../../constants/routes"
import BackdropContext from "../../../context/BackdropContext"
import StateContext from "../../../context/StateContext"

const CurrentChatMobile = ({ currentChat, setCurrentChat, setSnackbarMsg }) => {
  const navigate = useNavigate()

  const isFinished = currentChat.status !== "matched"

  const { backdropOpen, openBackdrop, closeBackdrop } = useContext(BackdropContext)
  const { userData } = useContext(UserContext)
  const userId = userData.user.id

  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showFertigDialog, setShowFertigDialog] = useState(false)
  const [msg, setMsg] = useState("")

  const [anchorEl, setAnchorEl] = useState(null)

  const [chatMessages, setChatMessages] = useState([])

  const { mutate: sendMutation, loading: sendLoading } = useMutation(`/chat/${currentChat?.chatId}`)
  const { mutate: cancelMutation, loading: cancelLoading } = useMutation("/ledger", "DELETE")
  const { mutate: finishMutation, loading: finishLoading } = useMutation("/ledger/finish")

  const { ledgerRefetch } = useContext(StateContext)

  const { data: chatMessagesRaw, loading: chatMessagesLoading } = useFetch(`/chat/${currentChat?.chatId}`, {
    skip: !currentChat || !currentChat?.chatId,
  })

  const loading = cancelLoading || finishLoading || chatMessagesLoading

  useEffect(() => {
    if (loading) {
      openBackdrop()
    } else {
      if (backdropOpen) {
        closeBackdrop()
      }
    }
  }, [loading, openBackdrop, backdropOpen, closeBackdrop])

  useEffect(() => {
    if (chatMessagesRaw && !chatMessagesLoading) {
      setChatMessages(JSON.parse(chatMessagesRaw.messages))
    } else {
      setChatMessages([])
    }
  }, [chatMessagesRaw, chatMessagesLoading])

  const handleFinish = async (preis) => {
    if (!currentChat) {
      return
    }

    try {
      await finishMutation({
        ...currentChat, // just for backend validation
        id: currentChat.id, // only these fields are really needed
        priceNegotiated: preis,
      })

      navigate(F_TRADING_HISTORY)
      setSnackbarMsg("Abgeschlossen!")
    } catch (e) {
      console.log(e)
    }

    setCurrentChat(null)
    ledgerRefetch()
  }

  const handleCancelPosition = async () => {
    if (!currentChat) {
      return
    }

    try {
      await cancelMutation({
        ...currentChat, // just for backend validation
        id: currentChat.id, // only these fields are really needed
      })

      const txt = currentChat.action === "buy" ? "Kauf" : "Verkauf"
      setSnackbarMsg(`${txt} abgebrochen`)
    } catch (e) {
      console.log(e)
    }

    setCurrentChat(null)
    ledgerRefetch()
  }

  const cancelDialog = () => {
    setShowFertigDialog(false)
    setShowCancelDialog(false)
  }

  const handleSend = async () => {
    setMsg("")

    try {
      const res = await sendMutation({
        senderId: userId,
        text: msg,
        // timestamp: Date.now().toLocaleString(),
      })

      if (res.messages) {
        setChatMessages(JSON.parse(res.messages))
      }
    } catch (e) {
      console.log(e.message)
    }
  }

  return (
    <>
      <Grid
        container
        direction="column"
        sx={{
          height: "100dvh", // 👈 important for mobile
          overflow: "hidden",
        }}>
        <Grid container direction="column" justifyContent="space-between" sx={{ height: "100%", pb: 1 }}>
          <Grid xs="auto" item container justifyContent="space-between" alignItems="center" sx={{ display: "inline-flex", ml: 5, py: 0.5 }}>
            <BookTwoToneIcon fontSize="medium" sx={{ color: !isFinished ? "primary.main" : "inherit" }} />

            <Grid item xs sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  cursor: "default",
                  color: !isFinished ? "inherit" : "grey",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {currentChat.book.title} {!isFinished ? "" : "• Fertig"}
              </Typography>
            </Grid>

            {!isFinished && (
              <Grid item xs="auto">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <MoreVertIcon />
                </IconButton>

                <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                  <MenuItem onClick={() => setShowFertigDialog(true)}>Abschließen</MenuItem>
                  <MenuItem onClick={() => setShowCancelDialog(true)}>Abbrechen</MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>

          <Grid item xs="auto">
            <Divider />
          </Grid>

          <Grid xs="auto" item container justifyContent="space-between" alignItems="center">
            <IconButton sx={{ p: 0.5 }} onClick={() => navigate(F_TRADING_CHAT)}>
              <ArrowBackIcon />
            </IconButton>
            <Grid item xs>
              <Typography variant="body2" onClick={() => navigate(F_TRADING_CHAT)}>
                Zur Übersicht
              </Typography>
            </Grid>

            {isFinished && !!currentChat.priceNegotiated && (
              <Typography variant="body1" align="right" sx={{ color: "grey" }}>
                {currentChat?.priceNegotiated + " €"}
              </Typography>
            )}
          </Grid>

          <Grid item xs="auto">
            <Divider />
          </Grid>

          <Grid
            item
            xs
            sx={{
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}>
            <Stack
              id="chat"
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                px: 1,
                pt: 1,
                pb: "80px",
              }}>
              {!loading && !chatMessages.length && (
                <Box sx={{ backgroundColor: "#eee", mt: 2, p: 2 }}>
                  <Typography textAlign="left">Hier kannst du mit deinem Tausch-Partner schreiben</Typography>
                  <Typography sx={{ mt: 2 }} textAlign="left">
                    Sobald du mit dem {currentChat.action === "buy" ? "Kauf" : "Verkauf"} fertig bist, klick in dem Menü auf "Abschließen"
                  </Typography>
                  <Typography sx={{ mt: 1 }} textAlign="left">
                    Wenn dein Tausch-Partner nicht reagiert oder sonstige Probleme auftreten, klick auf "Abbrechen"
                  </Typography>
                </Box>
              )}

              {chatMessages.map((msg, index) => (
                <Typography
                  key={index}
                  align={userId === msg.SenderId ? "right" : "left"}
                  sx={{
                    wordWrap: "break-word",
                    border: "1px solid gray",
                    borderRadius: "20px",
                    borderBottomLeftRadius: userId === msg.SenderId ? "20px" : "0",
                    borderBottomRightRadius: userId === msg.SenderId ? "0" : "20px",
                    height: "auto",
                    width: "auto",
                    maxWidth: "60%",
                    py: "1vh",
                    px: 2,
                    my: "1vh",
                    ml: userId === msg.SenderId ? "auto" : "0",
                    mr: userId === msg.SenderId ? "0" : "auto",
                    backgroundColor: userId === msg.SenderId ? "primary.main" : "#052D4E",
                    color: "#FFF",
                  }}>
                  {msg.Text}
                </Typography>
              ))}
            </Stack>
          </Grid>

          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              px: 1,
              pb: 2,
              pt: 1,
              backgroundColor: "background.default",
            }}>
            <Input
              fullWidth
              id="msg"
              name="msg"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && msg.trim().length > 0) {
                  handleSend()
                }
              }}
              disabled={sendLoading || isFinished}
              disableUnderline
              sx={{
                border: "2px solid",
                borderColor: isFinished || msg.trim() === "" ? "grey" : "primary.main",
                borderRadius: "20px",
                height: "48px",
                pl: 2,
                pr: 1,
                backgroundColor: "#fff",
                visibility: isFinished ? "hidden" : "visible",
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton onClick={() => msg.trim().length > 0 && handleSend()}>
                    <SendIcon sx={{ color: isFinished || msg.trim() === "" ? "grey" : "primary.main" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Grid>

        {(showFertigDialog || showCancelDialog) && (
          <ChatDialog
            showFertigDialog={showFertigDialog}
            showCancelDialog={showCancelDialog}
            handleCancelPosition={handleCancelPosition}
            cancelDialog={cancelDialog}
            handleFinish={handleFinish}
            currentChat={currentChat}
          />
        )}
      </Grid>
    </>
  )
}

export default CurrentChatMobile
